import { useMemo } from 'react';
import { Box } from '@mui/material';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { Table, useOnScrollDown } from 'redesign';
import { RenderUnsettledUI } from 'shared/components';
import { useTranslation } from 'react-i18next';
import { useGetEmployeeCertificates } from './hooks/use-get-employee-certificates';
import { getCertificatesColumnDefinition } from './utils/get-certificates-column-definition';
import {
  CertificateTableRow,
  GetEmployeeCertificatesResponse,
} from './employee-certificates-tab.types';

type Props = {
  flexWorkerId: string;
};

export const EmployeeCertificatesTab = ({ flexWorkerId }: Props) => {
  const certificatesQuery = useGetEmployeeCertificates(flexWorkerId);

  if (certificatesQuery.status !== 'success') {
    return <RenderUnsettledUI data={certificatesQuery} />;
  }

  return <EmployeeCertificatesTable infiniteQueryResult={certificatesQuery} />;
};

const EmployeeCertificatesTable = ({
  infiniteQueryResult,
}: {
  infiniteQueryResult: InfiniteQueryObserverSuccessResult<GetEmployeeCertificatesResponse, unknown>;
}) => {
  const { t } = useTranslation('web-common');
  const flatData = useMemo(
    () => infiniteQueryResult.data.pages.flatMap((page) => page.items),
    [infiniteQueryResult.data]
  );

  const columns = useMemo(() => getCertificatesColumnDefinition(t), [t]);

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult,
    flatData,
  });

  return (
    <Box maxHeight={'100%'}>
      <Table<CertificateTableRow>
        stickyHeader={true}
        columns={columns}
        data={flatData}
        tableStyle={{ tableLayout: 'fixed' }}
        onScroll={fetchIfBottomScrolled}
      />
    </Box>
  );
};
